import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Video, VideoOff, Mic, MicOff, Play, CheckCircle, Clock, AlertCircle, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InterviewRoom() {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('setup'); // setup | intro | question | recording | waiting | complete
  const [currentQ, setCurrentQ] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [recordTimer, setRecordTimer] = useState(60);
  const [chatMessages, setChatMessages] = useState([]);

  // Media state
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [stream, setStream] = useState(null);
  const [mediaError, setMediaError] = useState('');

  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const token = localStorage.getItem('auth_token');

  // Load interview data
  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/interviews/${interviewId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!r.ok) throw new Error('Interview not found');
        const d = await r.json();
        setInterview(d);
      } catch (e) {
        setMediaError(e.message);
      }
      setLoading(false);
    };
    load();
  }, [interviewId, token]);

  // Request camera + mic
  const enableMedia = useCallback(async () => {
    try {
      setMediaError('');
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setStream(s);
      setVideoEnabled(true);
      setAudioEnabled(true);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (err) {
      setMediaError('Camera and microphone access is required. Please allow permissions and try again.');
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) stream.getTracks().forEach(t => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [stream]);

  const addAgentMsg = (text) => setChatMessages(prev => [...prev, { from: 'agent', text, time: new Date() }]);

  // Start interview session
  const startInterview = async () => {
    try {
      const r = await fetch(`${API_URL}/api/interviews/${interviewId}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      setInterview(prev => ({ ...prev, ...d, status: 'in_progress' }));
      setPhase('intro');

      const qCount = d.question_count || interview?.questions?.length || 0;
      addAgentMsg(`Welcome to your interview for ${interview?.job_title || 'this position'}!`);
      setTimeout(() => addAgentMsg(`I will ask you ${qCount} questions. ${d.evaluation_criteria || ''}`), 1500);
      setTimeout(() => addAgentMsg(`For each question, you'll have 3 seconds to prepare, then 1 minute to answer via voice. Your video and audio are being recorded.`), 3000);
      setTimeout(() => addAgentMsg(`When you're ready, click "I'm Ready" to begin.`), 4500);
    } catch (e) {
      setMediaError('Failed to start interview');
    }
  };

  // Start first/next question
  const startQuestion = useCallback(() => {
    const questions = interview?.questions || [];
    if (currentQ >= questions.length) {
      completeInterview();
      return;
    }

    setPhase('question');
    addAgentMsg(`Question ${currentQ + 1} of ${questions.length}:`);
    setTimeout(() => {
      addAgentMsg(questions[currentQ]?.text || 'No question text');
      // 3 second countdown
      setPhase('waiting');
      setCountdown(3);
      let c = 3;
      const cd = setInterval(() => {
        c--;
        setCountdown(c);
        if (c <= 0) {
          clearInterval(cd);
          startRecording();
        }
      }, 1000);
    }, 800);
  }, [currentQ, interview]);

  // Start recording
  const startRecording = useCallback(() => {
    setPhase('recording');
    setRecordTimer(60);
    chunksRef.current = [];

    if (stream) {
      try {
        const mr = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus' });
        mr.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        mr.start(1000);
        recorderRef.current = mr;
      } catch {
        try {
          const mr = new MediaRecorder(stream, { mimeType: 'video/webm' });
          mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
          mr.start(1000);
          recorderRef.current = mr;
        } catch (err) {
          setMediaError('Recording failed: ' + err.message);
          return;
        }
      }
    }

    // 60 second timer
    let remaining = 60;
    timerRef.current = setInterval(() => {
      remaining--;
      setRecordTimer(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        stopRecording();
      }
    }, 1000);
  }, [stream]);

  // Stop recording and upload
  const stopRecording = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);

    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }

    // Wait a bit for final chunks
    await new Promise(res => setTimeout(res, 500));

    const blob = new Blob(chunksRef.current, { type: 'video/webm' });
    if (blob.size > 0) {
      const formData = new FormData();
      formData.append('question_index', currentQ);
      formData.append('recording', blob, `q${currentQ}_${interviewId}.webm`);

      try {
        await fetch(`${API_URL}/api/interviews/${interviewId}/submit-recording`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      } catch (e) {
        console.error('Upload failed:', e);
      }
    }

    addAgentMsg(`Thank you for your answer to Question ${currentQ + 1}.`);

    const questions = interview?.questions || [];
    const nextQ = currentQ + 1;
    if (nextQ >= questions.length) {
      setTimeout(() => {
        addAgentMsg("That was the last question. Let me wrap up your interview...");
        setTimeout(() => completeInterview(), 2000);
      }, 1500);
    } else {
      setTimeout(() => {
        addAgentMsg(`Moving to the next question...`);
        setCurrentQ(nextQ);
        setTimeout(() => {
          setPhase('question');
          addAgentMsg(`Question ${nextQ + 1} of ${questions.length}:`);
          setTimeout(() => {
            addAgentMsg(questions[nextQ]?.text || '');
            setPhase('waiting');
            setCountdown(3);
            let c = 3;
            const cd = setInterval(() => {
              c--;
              setCountdown(c);
              if (c <= 0) {
                clearInterval(cd);
                startRecording();
              }
            }, 1000);
          }, 800);
        }, 500);
      }, 2000);
    }
  }, [currentQ, interview, interviewId, token, startRecording]);

  const completeInterview = async () => {
    setPhase('complete');
    try {
      await fetch(`${API_URL}/api/interviews/${interviewId}/complete-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      addAgentMsg("Your interview is complete! The AI evaluation will process your answers shortly. Thank you for your time!");
    } catch {
      addAgentMsg("Interview recorded. There was a small hiccup completing the session, but your answers are saved.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 dark:text-red-400 mx-auto mb-3" />
          <p className="text-slate-900 dark:text-white text-lg mb-2">Interview Not Found</p>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">{mediaError || 'This interview may have expired or been cancelled.'}</p>
          <button onClick={() => navigate('/applicant')} className="mt-4 px-4 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white rounded-lg text-sm">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const questions = interview?.questions || [];
  const progress = phase === 'complete' ? 100 : questions.length > 0 ? Math.round((currentQ / questions.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex flex-col" data-testid="interview-room">
      {/* Header */}
      <div className="border-b border-slate-200 dark:border-zinc-800/50 px-6 py-3 flex items-center justify-between bg-white dark:bg-[#0e0e11]">
        <div>
          <h1 className="text-slate-900 dark:text-white font-semibold text-sm">{interview.job_title || 'Interview'}</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-xs">{interview.company || 'Numuv AI Interview'}</p>
        </div>
        <div className="flex items-center gap-4">
          {phase === 'recording' && (
            <div className="flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/30 rounded-full">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-red-600 dark:text-red-400 text-xs font-mono">{recordTimer}s</span>
            </div>
          )}
          <div className="text-slate-500 dark:text-zinc-500 text-xs">Q {Math.min(currentQ + 1, questions.length)} / {questions.length}</div>
          <div className="w-24 h-1.5 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Video feed - left */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 relative">
          <div className="relative w-full max-w-2xl aspect-video bg-slate-50 dark:bg-zinc-900 rounded-2xl overflow-hidden border border-slate-200 dark:border-zinc-800">
            <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
            {!videoEnabled && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-50 dark:bg-zinc-900">
                <div className="text-center">
                  <VideoOff className="w-12 h-12 text-slate-500 dark:text-zinc-600 mx-auto mb-3" />
                  <p className="text-slate-500 dark:text-zinc-500 text-sm">Camera is off</p>
                </div>
              </div>
            )}
            {/* Recording indicator */}
            {phase === 'recording' && (
              <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-red-600/90 rounded-lg">
                <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse" />
                <span className="text-slate-900 dark:text-white text-xs font-medium">REC</span>
              </div>
            )}
            {/* Countdown overlay */}
            {phase === 'waiting' && countdown > 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                <div className="text-center">
                  <p className="text-8xl font-bold text-slate-900 dark:text-white animate-pulse">{countdown}</p>
                  <p className="text-slate-500 dark:text-zinc-400 text-sm mt-2">Get ready to answer...</p>
                </div>
              </div>
            )}
          </div>

          {/* Media controls */}
          <div className="flex items-center gap-3 mt-4">
            <button onClick={() => { if (stream) { stream.getVideoTracks().forEach(t => { t.enabled = !t.enabled; }); setVideoEnabled(v => !v); } }}
              className={`p-3 rounded-full ${videoEnabled ? 'bg-slate-100 dark:bg-zinc-800 text-white' : 'bg-red-500/20 text-red-600 dark:text-red-400'}`}>
              {videoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
            </button>
            <button onClick={() => { if (stream) { stream.getAudioTracks().forEach(t => { t.enabled = !t.enabled; }); setAudioEnabled(a => !a); } }}
              className={`p-3 rounded-full ${audioEnabled ? 'bg-slate-100 dark:bg-zinc-800 text-white' : 'bg-red-500/20 text-red-600 dark:text-red-400'}`}>
              {audioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Chat panel - right */}
        <div className="w-[380px] border-l border-slate-200 dark:border-zinc-800/50 flex flex-col bg-white dark:bg-[#0e0e11]">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-zinc-800/50 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-slate-900 dark:text-white text-sm font-medium">Interview Agent</span>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="interview-chat">
            {chatMessages.map((msg, i) => (
              <div key={i} className="flex gap-2" style={{ animation: 'fadeSlideUp 0.3s ease-out' }}>
                <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 dark:text-blue-400 text-[10px] font-bold">AI</span>
                </div>
                <div className="bg-slate-100 dark:bg-zinc-800/50 rounded-xl rounded-tl-none px-3 py-2 max-w-[90%]">
                  <p className="text-slate-700 dark:text-zinc-200 text-sm leading-relaxed">{msg.text}</p>
                </div>
              </div>
            ))}
            {phase === 'recording' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-500/5 border border-red-500/20 rounded-lg">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-red-600 dark:text-red-400 text-xs">Recording your answer... {recordTimer}s remaining</span>
              </div>
            )}
          </div>

          {/* Action area */}
          <div className="px-4 py-4 border-t border-slate-200 dark:border-zinc-800/50">
            {phase === 'setup' && (
              <div className="space-y-3">
                {mediaError && <p className="text-red-600 dark:text-red-400 text-xs">{mediaError}</p>}
                {!videoEnabled || !audioEnabled ? (
                  <button onClick={enableMedia} data-testid="enable-media-btn"
                    className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-500 flex items-center justify-center gap-2">
                    <Video className="w-4 h-4" /> Enable Camera & Microphone
                  </button>
                ) : (
                  <button onClick={startInterview} data-testid="start-interview-btn"
                    className="w-full py-3 bg-emerald-600 text-white rounded-xl font-medium text-sm hover:bg-emerald-500 flex items-center justify-center gap-2">
                    <Play className="w-4 h-4" /> Start Interview
                  </button>
                )}
                <p className="text-slate-500 dark:text-zinc-600 text-xs text-center">You must enable camera and microphone before starting</p>
              </div>
            )}
            {phase === 'intro' && (
              <button onClick={() => { setCurrentQ(0); startQuestion(); }} data-testid="im-ready-btn"
                className="w-full py-3 bg-emerald-600 text-white rounded-xl font-medium text-sm hover:bg-emerald-500 flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4" /> {"I'm Ready — Start Questions"}
              </button>
            )}
            {(phase === 'question' || phase === 'waiting') && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="text-amber-600 dark:text-amber-400 text-sm">{countdown > 0 ? `Recording starts in ${countdown}s...` : 'Preparing...'}</span>
              </div>
            )}
            {phase === 'recording' && (
              <div className="space-y-2">
                <div className="w-full bg-slate-100 dark:bg-zinc-800 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-red-500 rounded-full transition-all duration-1000" style={{ width: `${((60 - recordTimer) / 60) * 100}%` }} />
                </div>
                <p className="text-slate-500 dark:text-zinc-500 text-xs text-center">Speak your answer clearly • {recordTimer}s remaining</p>
              </div>
            )}
            {phase === 'complete' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-emerald-600 dark:text-emerald-400 text-sm">Interview Complete!</span>
                </div>
                <button onClick={() => navigate('/applicant')} className="w-full py-2.5 bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white rounded-xl text-sm hover:bg-slate-200 dark:hover:bg-zinc-700">
                  Back to Dashboard
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
